import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Solid-Liquid Separation",
    title: "Stainless Steel Mesh & Wedge Wire for Solid-Liquid Separation",
    lead: "Engineered continuous-slot wedge wire screens and heavy-duty woven mesh designed for slurry dewatering, solids recovery, and industrial liquid clarification. Self-relieving V-shaped profile wires prevent particulate blinding while maintaining structural rigidity under process pressure.",
    points: ["Non-clogging V-wire profile provides two-point particle contact to discharge solids rapidly.", "Continuous slot openings from 20 µm to 3,000 µm with tight tolerance across the screen face.", "Heavy load-bearing support rods withstand differential pressure spikes and centrifuge shear."],
    facts: [{
      label: "Wire Profile",
      value: "Non-Clogging V-Wire"
    }, {
      label: "Slot Range",
      value: "20 µm – 3,000 µm"
    }, {
      label: "Slot Accuracy",
      value: "±5 µm (from 20 µm)"
    }, {
      label: "Alloy Grades",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "Stainless steel wedge wire dewatering screen panel showing precision continuous slot profiles"
  },
  zh: {
    eyebrow: "固液分离与矿浆脱水",
    title: "固液分离与矿浆脱水精密筛网",
    lead: "专为高浓度悬浮矿浆、工业废水与物料脱水打造的高强度连续缝隙筛网。采用自释形 V 型截面丝焊接构型，在耐受工况压差与物料冲刷的同时从根源消除孔隙堵塞。",
    points: ["两点微接触自释形 V 型丝截面使截留固体快速释放，根除堵网隐患。", "连续缝隙开孔精确覆盖 20 µm 至 3,000 µm，整板缝隙公差均一稳定。", "重载焊接筋条骨架承受水锤脉冲与离心剪切，维持长周期结构刚度。"],
    facts: [{
      label: "型线几何",
      value: "自释形 V 型丝"
    }, {
      label: "缝隙开孔",
      value: "20 µm – 3,000 µm"
    }, {
      label: "缝隙公差",
      value: "±5 µm（20 µm 起）"
    }, {
      label: "标准材质",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "展示高精度连续缝隙截面的不锈钢楔形丝脱水筛板"
  },
  es: {
    eyebrow: "Separación sólido-líquido",
    title: "Mallas de acero inoxidable y alambre en cuña para separación sólido-líquido",
    lead: "Cribas de alambre en cuña de ranura continua y telas metálicas pesadas para deshidratación de lodos, recuperación de sólidos y clarificación industrial. Los perfiles en V autolimpiantes evitan la colmatación bajo presión de proceso.",
    points: ["Perfil en V anticolmatante con contacto en dos puntos que descarga sólidos con rapidez.", "Ranuras continuas calibradas de 20 µm a 3.000 µm con estricta uniformidad dimensional.", "Varillas de soporte robustas para soportar picos de presión diferencial y fuerzas centrífugas."],
    facts: [{
      label: "Perfil de alambre",
      value: "Alambre en V anticolmatante"
    }, {
      label: "Rango de ranura",
      value: "20 µm – 3.000 µm"
    }, {
      label: "Precisión ranura",
      value: "±5 µm (desde 20 µm)"
    }, {
      label: "Aleaciones",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "Panel de cribado de deshidratación en alambre en cuña de acero inoxidable con ranura continua"
  },
  de: {
    eyebrow: "Fest-Flüssig-Trennung",
    title: "Edelstahldrahtgewebe & Spaltsiebe für Fest-Flüssig-Trennung",
    lead: "Kontinuierliche Spaltsiebe und schwere Drahtgewebe für Schlammentwässerung, Feststoffrückgewinnung und industrielle Klärung. Selbstreinigende V-Profildrähte verhindern Verstopfung und bewahren die Geometrie unter Betriebsdruck.",
    points: ["Selbstreinigendes V-Drahtprofil mit Zweipunktkontakt für raschen Feststoffaustrag.", "Kontinuierliche Spaltweiten von 20 µm bis 3.000 µm mit engen Fertigungstoleranzen.", "Stabile Trägerprofile widerstehen Scherkräften und zyklischen Druckstößen."],
    facts: [{
      label: "Drahtprofil",
      value: "Verstopfungsfreier V-Draht"
    }, {
      label: "Spaltweiten",
      value: "20 µm – 3.000 µm"
    }, {
      label: "Schlitzgenauigkeit",
      value: "±5 µm (ab 20 µm)"
    }, {
      label: "Werkstoffe",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "Edelstahl-Spaltsiebplatte zur Entwässerung mit präzisen kontinuierlichen Schlitzprofilen"
  },
  fr: {
    eyebrow: "Séparation solide-liquide",
    title: "Toiles inox et grilles à fil profilé pour séparation solide-liquide",
    lead: "Grilles à fente continue en fil profilé et toiles métalliques lourdes conçues pour l'égouttage des boues, la récupération de solides et la clarification. Le profil en V auto-décolmatant empêche le colmatage tout en résistant aux contraintes hydrauliques.",
    points: ["Profil en V auto-décolmatant assurant un contact en deux points pour évacuer les solides.", "Fentes continues de 20 µm à 3 000 µm usinées avec une tolérance rigoureuse.", "Barres de renfort soudées résistant aux coups de bélier et contraintes centrifuges."],
    facts: [{
      label: "Profil de fil",
      value: "Fil en V anti-colmatant"
    }, {
      label: "Plage de fente",
      value: "20 µm – 3 000 µm"
    }, {
      label: "Précision de fente",
      value: "±5 µm (dès 20 µm)"
    }, {
      label: "Nuances d'alliage",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "Panneau d'égouttage à fil profilé en acier inoxydable avec fentes continues de précision"
  },
  pt: {
    eyebrow: "Separação sólido-líquido",
    title: "Telas de aço inoxidável e arame em cunha para separação sólido-líquido",
    lead: "Telas de arame em cunha com ranhura contínua e telas metálicas para desidratação de polpas, recuperação de sólidos e clarificação industrial. Fios com perfil em V autolimpantes evitam o entupimento sob pressão de processo.",
    points: ["Perfil em V autolimpante com contato em dois pontos para descarga rápida de sólidos.", "Ranhuras contínuas de 20 µm a 3.000 µm com precisão dimensional consistente.", "Barras de reforço soldadas projetadas para absorver pulsos e forças de cisalhamento."],
    facts: [{
      label: "Perfil de arame",
      value: "Arame em V antientupimento"
    }, {
      label: "Faixa de abertura",
      value: "20 µm – 3.000 µm"
    }, {
      label: "Precisão de fenda",
      value: "±5 µm (a partir de 20 µm)"
    }, {
      label: "Ligas padrão",
      value: "304 / 316L / 2205"
    }],
    imageAlt: "Painel de desidratação em arame perfilado em cunha com ranhuras contínuas de precisão"
  }
};
export default function SolidLiquidPanel() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="solid-liquid" href="/SolidLiquidSeparationPage" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-eaf99e.png",
    alt: copy.imageAlt
  }} tone="background" />;
}